import { ArrowUpDown, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { MoveTierLevelModal } from '../components/MoveTierLevelModal';
import { OrgUnitTypeModal } from '../components/OrgUnitTypeModal';
import { orgUnitTypesApi } from '../organizationApi';
import type { OrgUnitTypeListItem } from '../organizationTypes';

/**
 * The tier ladder — one of the surfaces Organization → Settings leads to.
 *
 * **A tier is configuration rather than a record**, which is why it is reached through
 * Settings instead of getting a sidebar entry of its own. The chart is where units are
 * managed; the ladder the chart draws them against is a setting of the module. The chart's
 * own cog governs how the chart is *drawn* — orientation, which records show — and the
 * ladder is not that.
 *
 * **It is an `<IndexPage>` like every other list in the product.** It was built as a
 * hand-rolled `<DataTablePanel>` inside a card, on the argument that a settings list has no
 * use for the index shell's gutter, selection and rail — and the result drew TWO settings
 * cogs (the page's own beside Create, and the panel's inside the search group) with Create on
 * the wrong side of the toolbar. A list is a list wherever it leads from: reaching Settings
 * does not make a surface a panel inside something else, and the shell is what keeps one
 * answer to where Create sits and where the cog lives. **Do not revert it to a panel.**
 *
 * The anchor opens the **profile**, not the edit form — every index page in this product is
 * partnered with one, and a tier being only four stored values is an argument for a thin
 * profile rather than for none. Edit is in the row menu, where the shell puts it.
 */
export function OrgTiersPage() {
  const navigate = useNavigate();

  const [editing, setEditing] = useState<OrgUnitTypeListItem | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    tier: OrgUnitTypeListItem;
  } | null>(null);
  /** The tier whose level is being moved, or null. */
  const [movingLevel, setMovingLevel] = useState<OrgUnitTypeListItem | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  /*
    No sort is sent, and that is deliberate: the server's own default for this list is
    `sortOrder` ascending, so the rows read top rung first rather than alphabetically. The
    ladder's order is the catalog's own, not a preference of this page.
  */
  const fetchTiers = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      orgUnitTypesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  /*
    Only what goes IN the kebab. Edit and Delete are the shell's canonical props below rather
    than entries written here — `<RowActionMenu>` fixes their order and their icons, and a
    hand-written pair drifts from every other row in the product.
  */
  const rowActions = useCallback(
    (tier: OrgUnitTypeListItem): MenuItem[] => [
      {
        key: `move-level-${tier.id}`,
        label: 'Move level',
        icon: <ArrowUpDown className="h-4 w-4" aria-hidden="true" />,
        permission: 'org-unit-types.update',
        // State: a deleted tier is not on the ladder, so it has no level to move.
        disabledReason: tier.isActive
          ? null
          : 'Restore this tier before moving it — a deleted tier is not on the ladder.',
        onClick: () => setMovingLevel(tier),
      },
      {
        key: `restore-${tier.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'org-unit-types.delete',
        disabledReason: tier.isActive ? 'This tier is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', tier }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await orgUnitTypesApi.deactivate(confirming.tier.id);
      else await orgUnitTypesApi.activate(confirming.tier.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<OrgUnitTypeListItem>
        module="org-unit-types"
        /*
          The breadcrumb IS the back-nav (reference §6), so Settings links — this page is one
          level inside it and ships no Back button of its own.
        */
        breadcrumb={[
          'Human Resources',
          'Organization',
          { label: 'Settings', to: '/organization/settings' },
          'Tiers',
        ]}
        /*
          The module key derives "org unit types", which is not what anyone calls these and
          would put the wrong word on the widest destructive action in the module.
        */
        moduleNoun="tiers"
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['OrgUnitType']}
        rowKey={(tier) => tier.id}
        fetch={fetchTiers}
        reloadKey={reloadKey}
        searchPlaceholder="Search tiers"
        emptyMessage="No tiers yet. Add the ones this organization uses — Division, Department, Section."
        onCreate={() => setCreating(true)}
        createPermission="org-unit-types.create"
        // The anchor opens the profile; Edit is in the row menu. The read key, not the write
        // one — opening a record is not changing it.
        onRowOpen={(tier) => navigate(`/organization/settings/tiers/${tier.id}`)}
        openPermission="org-unit-types.read"
        onEdit={(tier) => setEditing(tier)}
        editPermission="org-unit-types.update"
        rowActions={rowActions}
        onDelete={(tier) => setConfirming({ kind: 'deactivate', tier })}
        deletePermission="org-unit-types.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(tier) => (tier.isActive ? null : 'This tier is already deleted.')}
        onClearData={() => orgUnitTypesApi.clear()}
        // Its own key, never implied by `.delete`: deleting the tier in front of you and
        // deleting the whole ladder are different privileges.
        clearDataPermission="org-unit-types.clear-data"
        otherInfo={(tier) => tier}
      />

      {creating || editing !== null ? (
        <OrgUnitTypeModal
          open
          editing={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      {movingLevel ? (
        <MoveTierLevelModal
          open
          tier={movingLevel}
          onClose={() => setMovingLevel(null)}
          onMoved={() => {
            setMovingLevel(null);
            // Every level from the move point down may have changed, so the page is refetched.
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete tier' : 'Restore tier'}
          danger={confirming.kind === 'deactivate'}
          // Both safeguards on the destructive half only. Restoring needs no ceremony.
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`tier-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.tier.name}" will leave the ladder, and its column will leave the chart.`
                  : `"${confirming.tier.name}" will return to the ladder.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.tier.unitCount > 0 ? (
                <p>
                  {confirming.tier.unitCount} unit
                  {confirming.tier.unitCount === 1 ? '' : 's'} still carr
                  {confirming.tier.unitCount === 1 ? 'ies' : 'y'} it. They are not deleted and
                  keep resolving its name, but they will have no tier to draw in until they are
                  moved.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted tier can be restored from this list.</p>

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<OrgUnitTypeListItem>[] = [
  {
    /*
      FIRST, because the first column after the `#` gutter is the ANCHOR — the row's identity
      and its click target. The hand-rolled panel led with Level, which reads as a ladder on
      a table and made the shell title every row in the list view with a bare number.
    */
    key: 'name',
    header: 'Tier',
    width: 220,
    flex: true,
    sortable: true,
    render: (tier) => <TextCell value={tier.name} />,
  },
  {
    /*
      Computed server-side from the whole ladder, so it is right on every page — the first
      row of page two is not level one. Not sortable: the server orders by the stored value
      behind it, and a header that silently sorted by something else would be a broken
      control.
    */
    key: 'level',
    header: 'Level',
    width: 80,
    align: 'right',
    /*
      NOT in the list view's summary line, where a column's value is printed with no header:
      a bare "1" between a timestamp and a unit count states nothing a reader can decode, and
      the list is already ordered by it. The table's header is what makes the number readable.
    */
    render: (tier) => <TextCell value={tier.level > 0 ? String(tier.level) : null} mono />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    summary: true,
    render: (tier) => <TextCell value={tier.description} />,
  },
  {
    key: 'unitCount',
    header: 'Units',
    width: 90,
    align: 'right',
    summary: true,
    // The count is of org units, which is another module's data — the tier's own name, level
    // and description beside it are this module's and stay ungated. Presentation only: the
    // DTO still carries the figure, which is the server's call to make, not the column's.
    permission: 'org-units.read',
    render: (tier) => <TextCell value={String(tier.unitCount)} mono numeric />,
  },
  {
    /*
      `status: true` is what moves it to the end of the DATA columns and what the list view
      reads to fill its right-hand badge. Without the flag the table happened to look right
      — it was declared last — and the list rendered no status at all, which on a list
      carrying Show deleted is the one state a row must never be silent about.
    */
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (tier) =>
      tier.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
